import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {ImpactPointTypes} from '@shared/enums/impact-point-types.enum';
import {VehicleNature} from '../../../claims/claims-commons/models/vehicle.model';

export enum InternalSection {
    FRONT = 'FRONT',
    MIDDLE = 'MIDDLE',
    REAR = 'REAR'
}

export enum InternalColumn {
    LEFT = 'LEFT',
    CENTER = 'CENTER',
    RIGHT = 'RIGHT'
}

@Injectable()
export class InternalImpactPointService {

    private onImpactPointsChangeSubject: Subject<ImpactPointTypes[]>;
    onImpactPointsChange: Observable<ImpactPointTypes[]>;

    impactPoints: ImpactPointTypes[] = [];
    nature: VehicleNature;
    canEdit: boolean;

    private readonly vehicles;

    constructor() {
        this.onImpactPointsChangeSubject = new Subject();
        this.onImpactPointsChange = this.onImpactPointsChangeSubject.asObservable();
        this.vehicles = {
            [VehicleNature.MOTORCYCLE]: {
                [InternalSection.MIDDLE]: {
                    [InternalColumn.CENTER]: [
                        ImpactPointTypes.REAR_SEAT,
                        ImpactPointTypes.FRONT_SEAT
                    ]
                }
            },
            [VehicleNature.MOTORVEHICLE]: {
                [InternalSection.REAR]: {
                    [InternalColumn.RIGHT]: [
                        ImpactPointTypes.REAR_BACK_R,
                        ImpactPointTypes.REAR_SEAT_R
                    ],
                    [InternalColumn.CENTER]: [
                        ImpactPointTypes.REAR_BACK,
                        ImpactPointTypes.REAR_SEAT
                    ],
                    [InternalColumn.LEFT]: [
                        ImpactPointTypes.REAR_BACK_L,
                        ImpactPointTypes.REAR_SEAT_L
                    ]
                },
                [InternalSection.FRONT]: {
                    [InternalColumn.RIGHT]: [
                        ImpactPointTypes.FRONT_BACK_R,
                        ImpactPointTypes.FRONT_SEAT_R
                    ],
                    [InternalColumn.LEFT]: [
                        ImpactPointTypes.FRONT_BACK_L,
                        ImpactPointTypes.FRONT_SEAT_L
                    ]
                }
            },
            [VehicleNature.VAN]: {
                [InternalSection.REAR]: {
                    [InternalColumn.RIGHT]: [
                        ImpactPointTypes.REAR_BACK_R,
                        ImpactPointTypes.REAR_SEAT_R
                    ],
                    [InternalColumn.CENTER]: [
                        ImpactPointTypes.REAR_BACK,
                        ImpactPointTypes.REAR_SEAT
                    ],
                    [InternalColumn.LEFT]: [
                        ImpactPointTypes.REAR_BACK_L,
                        ImpactPointTypes.REAR_SEAT_L
                    ]
                },
                [InternalSection.MIDDLE]: {
                    [InternalColumn.RIGHT]: [
                        ImpactPointTypes.MIDDLE_BACK_R,
                        ImpactPointTypes.MIDDLE_SEAT_R
                    ],
                    [InternalColumn.CENTER]: [
                        ImpactPointTypes.MIDDLE_BACK,
                        ImpactPointTypes.MIDDLE_SEAT
                    ],
                    [InternalColumn.LEFT]: [
                        ImpactPointTypes.MIDDLE_BACK_L,
                        ImpactPointTypes.MIDDLE_SEAT_L
                    ]
                },
                [InternalSection.FRONT]: {
                    [InternalColumn.RIGHT]: [
                        ImpactPointTypes.FRONT_BACK_R,
                        ImpactPointTypes.FRONT_SEAT_R
                    ],
                    [InternalColumn.CENTER]: [
                        ImpactPointTypes.FRONT_BACK,
                        ImpactPointTypes.FRONT_SEAT
                    ],
                    [InternalColumn.LEFT]: [
                        ImpactPointTypes.FRONT_BACK_L,
                        ImpactPointTypes.FRONT_SEAT_L
                    ]
                }
            }
        };
    }

    getSections(nature: VehicleNature): InternalSection[] {
        return <InternalSection[]>Object.keys(this.vehicles[nature]);
    }

    getColumns(nature: VehicleNature, section: InternalSection): InternalColumn[] {
        return <InternalColumn[]>Object.keys(this.vehicles[nature][section]);
    }

    getSeats(nature: VehicleNature, section: InternalSection, column: InternalColumn): ImpactPointTypes[] {
        return this.vehicles[nature][section][column];
    }

    isActive(impactPointType: ImpactPointTypes): boolean {
        return this.impactPoints.indexOf(impactPointType) > -1;
    }

    setPoint(impactPointType): void {
        this.impactPoints.push(impactPointType);
        this.onImpactPointsChangeSubject.next(this.impactPoints);
    }

    clearPoint(impactPointType): void {
        this.impactPoints.splice(this.impactPoints.indexOf(impactPointType), 1);
        this.onImpactPointsChangeSubject.next(this.impactPoints);
    }

    onSeatClick(seat: ImpactPointTypes): void {
        if (this.isActive(seat)) {
            this.clearPoint(seat);
        } else {
            this.setPoint(seat);
        }
    }
}
